import saveAs from "file-saver";
import moment from "moment";
import { PAYROLL_TABLE_COLS } from "pages/Payroll/payrollTable";
import { StationSpot } from "types/DataTypes";
import { PayrollRowData, PayrollFilters, PayrollUserData, PayrollUserDateList } from "types/Payroll";

const getRange = (start: number, stop: number, step = 1) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

export const downloadPayrollFile = (
  applyFilter: boolean | undefined,
  workbook: any,
  userStations: StationSpot[],
  payrollFilters: PayrollFilters,
) => {
  const stationName = applyFilter
    ? Number(payrollFilters.station)
      ? `${
          userStations.find(
            (spot: StationSpot) => spot.station.id === Number(payrollFilters.station),
          )?.station.tag
        }`
      : "All"
    : "All";

  const dateName =
    payrollFilters?.type === "week"
      ? `Week${payrollFilters?.week_date?.split("-W")[1]}-${payrollFilters?.week_date?.split("-W")[0]}`
      : payrollFilters?.type === "two_week"
        ? `Week${payrollFilters?.rangeDateOne?.split("-W")[1]}-${payrollFilters?.week_date?.split("-W")[0]}-Week${payrollFilters?.rangeDateTwo?.split("-W")[1]}-${payrollFilters?.week_date?.split("-W")[0]}`
        : `M${payrollFilters.month}-${payrollFilters.year}`;

  return workbook.xlsx.writeBuffer().then(function (data: any) {
    var blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Payroll-${stationName}-${dateName}.xlsx`);
  });
};

export const headPayrollRow = (worksheet: any) => {
  worksheet
    .addRow(PAYROLL_TABLE_COLS.map((col) => col.label))
    .eachCell((cell: any) => {
      cell.font = { bold: true, color: { argb: "black" }, size: 12 };
      cell.font = { name: "Arial", family: 2, bold: false, size: 10 };
      cell.border = {
        top: { style: "thick" },
        left: { style: "thick" },
        bottom: { style: "thick" },
        right: { style: "thick" },
      };
    });
  return worksheet.columns.forEach((col: any, index: number) => {
    return (col.width = index === 0 ? 30 : 20);
  });
};

export const sumPayrollRow = (
  worksheet: any,
  data: PayrollRowData[],
  userData?: PayrollUserData | null,
  isTotalTable?: boolean | undefined,
) => {
  const documentRow = worksheet.addRow([
    `${userData ? `${userData.username} - ` : ""}Total Hours:`,
    `${userData ? userData.badge : ""}`,
    "",
    "",
    "",
    "",
    "",
    userData ? userData.rate : "",
    "",
    "",
    "",
  ]);

  const startRow = documentRow.number - data.length;
  const endRow = documentRow.number - 1;

  documentRow.getCell(7).value = { formula: `G${getRange(startRow, endRow).join("+G")}` };
  documentRow.getCell(9).value = { formula: `I${getRange(startRow, endRow).join("+I")}` };
  documentRow.getCell(10).value = { formula: `J${getRange(startRow, endRow).join("+J")}` };

  documentRow.eachCell((cell: any) => {
    cell.font = { bold: true, color: { argb: "black" }, size: 12 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E0E0E0" } };
    cell.font = { name: "Arial", family: 2, bold: false, size: 10 };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  return documentRow;
};

export const getFilteredPayrollRows = (
  dataList: PayrollRowData[],
  sortId?: string,
  search = "",
  station?: number | string,
  userRole?: number,
  userStatus?: number,
  userId?: number | null,
) => {
  const query = search?.trim().toLowerCase();
  const stationId = Number(station);
  const columnId = sortId?.replace("-", "") || "user";
  const isDesc = !!sortId?.startsWith("-");

  const filtered = dataList.filter((row) => {
    if (userId && row.user_id !== userId) return false;
    if (query && !(row.user_name?.toLowerCase() || "").includes(query)) return false;
    if (stationId && row.station_id !== stationId) return false;
    if (userRole && row.user_role !== userRole) return false;
    if (userStatus && row.user_status !== userStatus) return false;
    return true;
  });

  return [...filtered].sort((a, b) => comparePayrollSortValues(a, b, columnId, isDesc));
};

export const getPayrollTotals = (rows: PayrollRowData[]) =>
  rows.reduce(
    (acc, row) => {
      acc.hours += Number(row.role_hours) || 0;
      acc.daPay += Number(row.pay) || 0;
      acc.agencyPay += Number(row.agency_pay) || 0;
      return acc;
    },
    { hours: 0, daPay: 0, agencyPay: 0 },
  );

const getPayrollWeekDate = (date: string) => moment(date).locale("en").format("gggg-[W]ww");

export const isPayrollRowSelected = (list: PayrollUserDateList[], row: PayrollRowData) => {
  const weekDate = getPayrollWeekDate(row.date);
  return list.some(
    (item) => item.id === row.user_id && item.week_date === weekDate && item.dates.includes(row.date),
  );
};

export const areAllPayrollRowsSelected = (list: PayrollUserDateList[], rows: PayrollRowData[]) =>
  rows.length > 0 && rows.every((row) => isPayrollRowSelected(list, row));

export const getPayrollUserDatesFromRows = (rows: PayrollRowData[]): PayrollUserDateList[] =>
  rows.reduce<PayrollUserDateList[]>((acc, row) => {
    const weekDate = getPayrollWeekDate(row.date);
    const existing = acc.find((item) => item.id === row.user_id && item.week_date === weekDate);
    if (!existing) {
      acc.push({ id: row.user_id, week_date: weekDate, dates: [row.date] });
    } else if (!existing.dates.includes(row.date)) {
      existing.dates.push(row.date);
    }
    return acc;
  }, []);

export const togglePayrollUserDate = (
  list: PayrollUserDateList[],
  userId: number,
  date: string,
): PayrollUserDateList[] => {
  const weekDate = getPayrollWeekDate(date);
  const existing = list.find((item) => item.id === userId && item.week_date === weekDate);
  if (!existing) {
    return [...list, { id: userId, week_date: weekDate, dates: [date] }];
  }
  if (existing.dates.includes(date)) {
    const dates = existing.dates.filter((item) => item !== date);
    return dates.length
      ? list.map((item) =>
          item.id === userId && item.week_date === weekDate ? { ...item, dates } : item,
        )
      : list.filter((item) => !(item.id === userId && item.week_date === weekDate));
  }
  return list.map((item) =>
    item.id === userId && item.week_date === weekDate
      ? { ...item, dates: [...item.dates, date] }
      : item,
  );
};

const comparePayrollSortValues = (
  a: PayrollRowData,
  b: PayrollRowData,
  columnId: string,
  isDesc: boolean,
) => {
  const aValue = getPayrollSortValue(a, columnId);
  const bValue = getPayrollSortValue(b, columnId);

  if (typeof aValue === "number" && typeof bValue === "number") {
    return isDesc ? bValue - aValue : aValue - bValue;
  }

  const result = String(aValue).localeCompare(String(bValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return isDesc ? -result : result;
};

const getPayrollSortValue = (row: PayrollRowData, columnId: string): string | number => {
  switch (columnId) {
    case "user":
      return row.user_name || "";
    case "badge":
      return row.badge || "";
    case "station":
      return row.station || "";
    case "agency":
      return row.agency || "";
    case "date":
      return row.date || "";
    case "role":
      return row.role || "";
    case "number_of_hours":
      return row.role_hours ?? "";
    case "rate":
      return row.pay_rate ?? "";
    case "da_pay":
      return row.pay ?? "";
    case "agency_pay":
      return row.agency_pay ?? "";
    case "note":
      return row.comment || "";
    default:
      return "";
  }
};
